import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { Box, Stack, Divider, Grid, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { CopyTextField } from 'components/CopyTextField/CopyTextField';
import {
  ErrorMessage,
  Field,
  FieldArray,
  FieldArrayRenderProps,
  Formik,
  FormikErrors,
} from 'formik';
import ScrollToFieldError from 'components/ScrollToFieldError/ScrollToFieldError';
import {
  useGetToolQuery,
  useCreateToolMutation,
  useUpdateToolMutation,
} from 'api';
import {
  Breadcrumbs,
  Button,
  Typography,
  COLORS,
  RadioGroup,
  Radio,
  TextField,
  TYPOGRAPHY_TOKENS,
} from 'pvs-design-system';
import {
  PAGE_TITLE_HEADER_TEXT_ID,
  REG_EXP,
  ROUTES,
} from 'constants/constants';
import DuplicateToolModal from 'components/DuplicateToolModal/DuplicateToolModal';
import DeleteParameterModal from 'components/DeleteParameterModal/DeleteParameterModal';
import LtiAdvantageSection from 'components/LtiAdvantageSection/LtiAdvantageSection';
import ToolDeploymentSection, {
  DeploymentParam,
} from 'components/ToolDeploymentSection/ToolDeploymentSection';
import * as Yup from 'yup';
import Form from 'components/Form/Form';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import ParameterTextField from 'components/ParameterTextField/ParameterTextField';
import { Parameter, Permission, Tool } from 'types';
import { Notify } from 'components/Notifications/Notifications';
import {
  buildLtiAdvantagePayload,
  parseLtiAdvantageData,
} from 'utils/ltiAdvantageHelper';
import generateUUID from 'utils/generateUUID';

const VERTICAL_SPACING = 2;
const BUTTON_SPACING = 2;

type CustomParam = {
  id: string;
  name: string;
  value: string;
};

type DuplicateDeploymentLocation = {
  existingDeploymentId: string;
  locationName: string;
};

const getNormalizedLocationId = (locationId: unknown): string =>
  String(locationId);

// Align radio buttons to the top
const StyledRadioGroup = styled(RadioGroup)`
  label {
    display: flex;
    align-items: flex-start;
  }
`;

const LabelSizeOverride = styled(Box)({
  '& span.MuiTypography-root': TYPOGRAPHY_TOKENS['p-sm'],
});

// Safe function to extract error message as string
const safeGetErrorMessage = (
  error: string | undefined | FormikErrors<unknown>
): string => {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  return '';
};

// Determine if a caught error is an HTTP 409 conflict (duplicate tool)
const isConflictError = (err: unknown): boolean =>
  Boolean(
    err &&
      typeof err === 'object' &&
      'status' in err &&
      (err as { status: number }).status === 409
  );

// Extract a human-readable error message from an RTK Query error body.
// Supports string bodies as well as `message` / `errorMessage` fields.
const getApiErrorMessage = (err: unknown): string | undefined => {
  if (!err || typeof err !== 'object') return undefined;

  const anyErr = err as { data?: unknown; error?: unknown; message?: unknown };

  // RTK Query can surface a top-level `error` string for network/parsing failures.
  if (typeof anyErr.error === 'string') return anyErr.error;
  // SerializedError commonly exposes `message`.
  if (typeof anyErr.message === 'string') return anyErr.message;

  const errorData = anyErr.data;
  if (typeof errorData === 'string') return errorData;

  if (errorData && typeof errorData === 'object') {
    const { message, errorMessage, errors } = errorData as {
      message?: unknown;
      errorMessage?: unknown;
      errors?: unknown;
    };

    if (typeof message === 'string') return message;
    if (typeof errorMessage === 'string') return errorMessage;
    if (typeof errors === 'string') return errors;
    if (Array.isArray(errors) && errors.every((e) => typeof e === 'string')) {
      return errors.join(', ');
    }
  }

  return undefined;
};

const getDuplicateDeploymentLocationMessage = (
  duplicateLocation: DuplicateDeploymentLocation
) => {
  const locationLabel = duplicateLocation.locationName
    ? `"${duplicateLocation.locationName}"`
    : 'this location';

  return (
    `The selected location ${locationLabel} is already assigned to deployment ` +
    `"${duplicateLocation.existingDeploymentId}". Please select a different location.`
  );
};

const getDuplicateDeploymentValidationErrors = (
  deployments?: DeploymentParam[]
) => {
  if (!deployments || deployments.length === 0) {
    return undefined;
  }

  const assignedLocations = new Map<
    string,
    { deploymentId: string; locationName: string }
  >();
  const deploymentErrors: Array<{ rootLocations: string } | undefined> = [];

  deployments.forEach((deployment, deploymentIndex) => {
    (deployment.rootLocations ?? []).forEach((location) => {
      const normalizedLocationId = getNormalizedLocationId(location.id);
      const existingAssignment = assignedLocations.get(normalizedLocationId);

      if (existingAssignment) {
        deploymentErrors[deploymentIndex] = {
          rootLocations: getDuplicateDeploymentLocationMessage({
            existingDeploymentId: existingAssignment.deploymentId,
            locationName: location.name || existingAssignment.locationName,
          }),
        };
        return;
      }

      assignedLocations.set(normalizedLocationId, {
        deploymentId: deployment.deploymentId,
        locationName: location.name,
      });
    });
  });

  return deploymentErrors.some(Boolean) ? deploymentErrors : undefined;
};

// Map a single deployment's root locations for the API payload
const mapRootLocationsForPayload = (deployment: DeploymentParam) =>
  deployment.rootLocations.length > 0
    ? deployment.rootLocations.map((rl) => ({
        id: rl.id,
        name: rl.name,
        path: rl.path,
        locationLevel: rl.locationLevel,
      }))
    : null;

// Map deployments (with their root locations) for the API payload
const mapDeploymentsForPayload = (deployments: DeploymentParam[]) =>
  deployments.map((deployment) => ({
    deploymentId: deployment.deploymentId,
    rootLocations: mapRootLocationsForPayload(deployment),
  }));

interface StaticParamRowProps {
  param: CustomParam;
  index: number;
  nameErrorMessage: string;
  valueErrorMessage: string;
  showNameError: boolean;
  showValueError: boolean;
  isEditMode: boolean;
  arrayHelpers: FieldArrayRenderProps;
  setFieldValue: (field: string, value: unknown) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
  onDeleteParameter: (
    arrayHelpers: FieldArrayRenderProps,
    index: number,
    isEditMode: boolean
  ) => void;
}

const StaticParamRow: React.FC<StaticParamRowProps> = ({
  param,
  index,
  nameErrorMessage,
  valueErrorMessage,
  showNameError,
  showValueError,
  isEditMode,
  arrayHelpers,
  setFieldValue,
  setFieldTouched,
  onDeleteParameter,
}) => (
  <Grid container spacing={2} alignItems="flex-start">
    <Grid item xs={6}>
      <Box>
        <ParameterTextField
          id={`staticCustomParameter.${index}.name`}
          label=""
          index={index}
          value={param.name}
          onChange={(e) =>
            setFieldValue(`staticCustomParameter.${index}.name`, e.target.value)
          }
          onBlur={() =>
            setFieldTouched(`staticCustomParameter.${index}.name`, true)
          }
          error={showNameError}
          errorMessage={nameErrorMessage}
          showDeleteIcon={false}
        />
      </Box>
    </Grid>
    <Grid
      item
      xs={6}
      sx={{
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ParameterTextField
          id={`staticCustomParameter.${index}.value`}
          label=""
          index={index}
          value={param.value}
          onChange={(e) =>
            setFieldValue(
              `staticCustomParameter.${index}.value`,
              e.target.value
            )
          }
          onBlur={() =>
            setFieldTouched(`staticCustomParameter.${index}.value`, true)
          }
          error={showValueError}
          errorMessage={valueErrorMessage}
          showDeleteIcon={false}
        />
      </Box>
      <IconButton
        id={`staticCustomParameter.${index}-delete-button`}
        aria-label="Delete parameter"
        sx={{
          ml: 1,
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
          padding: 0,
          alignSelf: 'center',
          '&:hover': {
            backgroundColor: 'transparent',
          },
          '&:focus': {
            backgroundColor: 'transparent',
          },
        }}
        onClick={() => onDeleteParameter(arrayHelpers, index, isEditMode)}
      >
        <DeleteIcon sx={{ color: '#722362', fontSize: 28 }} />
      </IconButton>
    </Grid>
  </Grid>
);

interface ParamValueRowProps {
  fieldName: string;
  param: CustomParam;
  index: number;
  valueErrorMessage: string;
  showValueError: boolean;
  arrayHelpers: FieldArrayRenderProps;
  setFieldValue: (field: string, value: unknown) => void;
  setFieldTouched: (field: string, touched: boolean) => void;
}

// eslint-disable-next-line react/no-multi-comp
const ParamValueRow: React.FC<ParamValueRowProps> = ({
  fieldName,
  param,
  index,
  valueErrorMessage,
  showValueError,
  arrayHelpers,
  setFieldValue,
  setFieldTouched,
}) => (
  <Box>
    <ParameterTextField
      id={`${fieldName}.${index}.value`}
      label="Parameter Name"
      index={index}
      value={param.value}
      onChange={(e) =>
        setFieldValue(`${fieldName}.${index}.value`, e.target.value)
      }
      onBlur={() => setFieldTouched(`${fieldName}.${index}.value`, true)}
      onDeleteClick={() => arrayHelpers.remove(index)}
      error={showValueError}
      errorMessage={valueErrorMessage}
    />
  </Box>
);

const toolFormValidationSchema = Yup.object().shape({
  id: Yup.string()
    .trim()
    .required('Tool Name is required')
    .max(16, 'Maximum of 16 characters')
    .matches(
      REG_EXP.lettersNumbersDashes,
      'Tool Name may only contain letters, numbers, and dashes.'
    ),
  description: Yup.string()
    .trim()
    .required('Description is required')
    .max(200, 'Maximum of 200 characters'),
  toolHostName: Yup.string()
    .trim()
    .required('Tool Hostname is required')
    .max(200, 'Maximum of 200 characters'),
  openIdConnectLoginUrl: Yup.string()
    .trim()
    .required('OpenID Connect Login URL is required')
    .max(255, 'Maximum of 255 characters')
    .url('OpenID Connect Login URL is invalid'),
  toolKeySetUrl: Yup.string()
    .trim()
    .required('Tool Key Set URL is required')
    .max(255, 'Maximum of 255 characters')
    .url('Tool Key Set URL is invalid'),
  toolRedirectUrl: Yup.string()
    .trim()
    .required('Tool Redirect URL is required')
    .max(255, 'Maximum of 255 characters')
    .matches(REG_EXP.toolRedirectUrl, 'Tool Redirect URL is invalid'),

  // Use Yup to validate the array of staticCustomParameter objects is either empty or has a value for each item in the array.
  staticCustomParameter: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string(),
        name: Yup.string().trim().required('Parameter name is required.'),
        value: Yup.string().trim().required('Parameter value is required.'),
      })
    )
    .test(
      'unique-static-custom-params',
      'Duplicate parameter name and value combination found',
      function uniqueStaticCustomParams(value) {
        if (!value || value.length === 0) return true;

        const combinations = new Set();
        let duplicateParam = null;

        for (let i = 0; i < value.length; i += 1) {
          const param = value[i];
          if (param && param.name && param.value) {
            const combination = `${param.name.trim()}|${param.value.trim()}`;
            if (combinations.has(combination)) {
              duplicateParam = {
                name: param.name.trim(),
                value: param.value.trim(),
              };
              break;
            }
            combinations.add(combination);
          }
        }

        // If we found duplicates, create error with specific parameter details
        if (duplicateParam) {
          return this.createError({
            path: 'staticCustomParameter',
            message: `Duplicate parameter found: "${duplicateParam.name}" = "${duplicateParam.value}"`,
          });
        }

        return true;
      }
    ),

  queryParameter: Yup.array().of(
    Yup.object().shape({
      id: Yup.string(),
      value: Yup.string().trim().required('Parameter name is required.'),
    })
  ),

  // Payload parameter validation with duplicate check
  payloadParameter: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string(),
        value: Yup.string().trim().required('Parameter name is required.'),
      })
    )
    .test(
      'unique-payload-params',
      'Duplicate parameter name found',
      function uniquePayloadParams(value) {
        if (!value || value.length === 0) return true;
        const paramNames = new Set();
        let duplicateParamName = null;

        for (let i = 0; i < value.length; i += 1) {
          const param = value[i];
          if (param.value) {
            const trimmedName = param.value.trim();
            if (paramNames.has(trimmedName)) {
              duplicateParamName = trimmedName;
              break;
            }
            paramNames.add(trimmedName);
          }
        }

        if (duplicateParamName) {
          return this.createError({
            path: 'payloadParameter',
            message: `Duplicate parameter found: "${duplicateParamName}"`,
          });
        }

        return true;
      }
    ),

  // Deployments validation - every deployment must have at least one root location.
  // The "Default" deployment is rendered separately (defaultDeploymentId) and is not
  // part of this array, so no index-based exception is needed.
  deployments: Yup.array().of(
    Yup.object().shape({
      deploymentId: Yup.string(),
      rootLocations: Yup.array().test(
        'at-least-one-location',
        'Must have at least one location.',
        function validateRootLocations(value) {
          if (!value || value.length === 0) {
            return this.createError({
              message: `Deployment "${this.parent?.deploymentId}" must have at least one location.`,
            });
          }
          return true;
        }
      ),
    })
  ),

  // LTI Advantage validation
  nrpsEnabled: Yup.boolean(),
  deepLinkingEnabled: Yup.boolean(),
  agsEnabled: Yup.boolean(),
});

// eslint-disable-next-line react/no-multi-comp
const ToolForm: React.FC<{ isEditMode: boolean }> = ({ isEditMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: tool,
    isSuccess: isDataFetched,
    isError,
    error,
  } = useGetToolQuery(id || '', {
    skip: !isEditMode,
  });
  const [createTool] = useCreateToolMutation();
  const [updateTool] = useUpdateToolMutation();
  const buttonText = isEditMode ? 'Save Changes' : 'Add Tool';
  const [isDuplicateToolModalOpen, setIsDuplicateToolModalOpen] =
    useState(false);
  const [isDeleteParameterModalOpen, setIsDeleteParameterModalOpen] =
    useState(false);
  const [parameterToDeleteIndex, setParameterToDeleteIndex] = useState<
    number | null
  >(null);
  const [currentArrayHelpers, setCurrentArrayHelpers] =
    useState<FieldArrayRenderProps | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [initialValues, setInitialValues] = useState({
    id: '',
    description: '',
    toolHostName: '',
    openIdConnectLoginUrl: '',
    toolKeySetUrl: '',
    toolRedirectUrl: '',
    oAuth2AccessTokenUrl: '',
    clientId: '',
    issuer: '',
    platformKeySetUrl: '',
    openIdConnectAuthUrl: '',
    staticCustomParameter: [] as CustomParam[],
    queryParameter: [] as CustomParam[],
    payloadParameter: [] as CustomParam[],
    permissions: [] as Permission[],
    userAccess: 'context',
    defaultDeploymentId: isEditMode ? '' : generateUUID(),
    deployments: [] as DeploymentParam[],
    nrpsEnabled: false,
    deepLinkingEnabled: false,
    agsEnabled: false,
  });

  const failureMessageVerb = isEditMode ? 'updated' : 'added';
  const failureMessage = `Tool could not be ${failureMessageVerb}. Please try again.`;

  const onSubmit = async (
    values: typeof initialValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    // Prepare the payload for the API
    const toolPayload: Tool = {
      id: values.id,
      description: values.description,
      toolHostName: values.toolHostName,
      openIdConnectLoginUrl: values.openIdConnectLoginUrl,
      toolKeySetUrl: values.toolKeySetUrl,
      toolRedirectUrl: values.toolRedirectUrl,
      oAuth2AccessTokenUrl: values.oAuth2AccessTokenUrl,
      authorization: values.userAccess,
      clientId: values.clientId,
      issuer: values.issuer,
      platformKeySetUrl: values.platformKeySetUrl,
      openIdConnectAuthUrl: values.openIdConnectAuthUrl,
      dynamicParameter: {
        deploymentId: values.defaultDeploymentId,
        deployments: mapDeploymentsForPayload(values.deployments),
        staticCustomParameter: values.staticCustomParameter.map(
          (param: CustomParam) => ({
            parameterName: param.name,
            parameterValue: param.value,
          })
        ),
        queryParameter: values.queryParameter.map(
          (param: CustomParam) => param.value
        ) as string[],
        payloadParameter: values.payloadParameter.map(
          (param: CustomParam) => param.value
        ) as string[],
        permissions: [
          { manage: `LTI13_MANAGE_${values.id.toUpperCase()}` },
          { launch: `LTI13_LAUNCH_${values.id.toUpperCase()}` },
        ],
        advantage: buildLtiAdvantagePayload(values),
      },
    };

    setIsLoading(true);

    try {
      if (isEditMode) {
        await updateTool(toolPayload as Partial<Tool>).unwrap();
      } else {
        await createTool(toolPayload as Partial<Tool>).unwrap();
      }
      resetForm();
      Notify.success(
        `Success, tool has been ${isEditMode ? 'updated' : 'added'}.`
      );
      navigate(ROUTES.tools);
    } catch (err) {
      if (isConflictError(err)) {
        setIsDuplicateToolModalOpen(true);
      } else {
        Notify.error(getApiErrorMessage(err) ?? failureMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function for static custom parameters (name-value pairs)
  const mapStaticCustomParams = (parameters: Parameter[]) => {
    return parameters.map((param, index) => {
      return {
        id: index.toString(),
        name: param.parameterName,
        value: param.parameterValue,
      };
    });
  };

  // Helper function for query/payload parameters
  // This function handles both string[] (from API) and Parameter[] (from interface)
  const mapStringParams = (parameters: string[] | Parameter[]) => {
    return parameters.map((param, index) => {
      // Check if param is a string or Parameter object
      if (typeof param === 'string') {
        // It's a string from the actual API
        return {
          id: index.toString(),
          name: '', // not used for query/payload parameters
          value: param,
        };
      }
      // It's a Parameter object (fallback for interface consistency)
      return {
        id: index.toString(),
        name: '', // not used for query/payload parameters
        value: param.parameterName || param.parameterValue || '',
      };
    });
  };

  // Load data into form
  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    if (isError) {
      navigate(ROUTES.createTool);
      return;
    }

    if (isDataFetched) {
      if (!tool) {
        // eslint-disable-next-line no-console
        console.warn('Tool data invalid');
        navigate(ROUTES.createTool);
        return;
      }

      setInitialValues({
        id: tool.id,
        description: tool.description,
        toolHostName: tool.toolHostName,
        openIdConnectLoginUrl: tool.openIdConnectLoginUrl,
        toolKeySetUrl: tool.toolKeySetUrl,
        toolRedirectUrl: tool.toolRedirectUrl,
        oAuth2AccessTokenUrl: tool.oAuth2AccessTokenUrl,
        clientId: tool.clientId,
        issuer: tool.issuer,
        platformKeySetUrl: tool.platformKeySetUrl,
        openIdConnectAuthUrl: tool.openIdConnectAuthUrl,
        staticCustomParameter: mapStaticCustomParams(
          tool.dynamicParameter?.staticCustomParameter || []
        ),
        queryParameter: mapStringParams(
          tool.dynamicParameter?.queryParameter || []
        ),
        payloadParameter: mapStringParams(
          tool.dynamicParameter?.payloadParameter || []
        ),
        permissions: tool.dynamicParameter.permissions || [],
        userAccess: tool.authorization,
        defaultDeploymentId: tool.dynamicParameter?.deploymentId || '',
        deployments: (tool.dynamicParameter?.deployments || []).map((d) => ({
          deploymentId: d.deploymentId,
          rootLocations: (d.rootLocations || []).map((rl) => ({
            id: rl.id,
            name: rl.name,
            path: rl.path,
            locationLevel: rl.locationLevel,
          })),
        })),
        // Load LTI Advantage data using helper
        ...parseLtiAdvantageData(tool.dynamicParameter.advantage),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tool, isEditMode, navigate, error, isDataFetched]);

  const deleteParameter = (
    arrayHelpers: FieldArrayRenderProps,
    index: number
  ) => {
    if (arrayHelpers !== null && index !== null) {
      arrayHelpers.remove(index);
    }
  };

  const handleParameterDeleteClick = (
    arrayHelpers: FieldArrayRenderProps,
    index: number,
    isFormInEditMode: boolean
  ) => {
    if (isFormInEditMode) {
      setParameterToDeleteIndex(index);
      setCurrentArrayHelpers(arrayHelpers);
      setIsDeleteParameterModalOpen(true);
    } else {
      deleteParameter(arrayHelpers, index);
    }
  };

  const resetDeleteParameterModal = () => {
    setParameterToDeleteIndex(null);
    setCurrentArrayHelpers(null);
    setIsDeleteParameterModalOpen(false);
  };

  const handleConfirmDeleteParameter = (
    arrayHelpers: FieldArrayRenderProps
  ) => {
    if (parameterToDeleteIndex !== null) {
      deleteParameter(arrayHelpers, parameterToDeleteIndex);
    }
    resetDeleteParameterModal();
  };

  return (
    <>
      <LoadingSpinner
        displayText="Loading LTI 1.3 Tool"
        showLoading={!isDataFetched && isEditMode}
      />
      {(!isEditMode || (isEditMode && isDataFetched)) && (
        <>
          <Breadcrumbs
            links={[
              {
                to: ROUTES.tools,
                isMobileLink: true,
                linkText: 'LTI 1.3 Tool Configuration',
                component: Link,
              },
              {
                to: ROUTES.tools,
                linkText: 'LTI 1.3 Tool Configuration',
                component: Link,
              },
              {
                href: '',
                linkText: isEditMode
                  ? 'Edit LTI 1.3 Tool'
                  : 'Add New LTI 1.3 Tool',
                component: Link,
              },
            ]}
          />
          <Typography
            id={PAGE_TITLE_HEADER_TEXT_ID}
            variant="h1"
            marginTop="40px"
            textAlign="left"
          >
            {isEditMode ? 'Edit' : 'Add New'} LTI 1.3 Tool
          </Typography>
          <Formik
            initialValues={initialValues}
            validationSchema={toolFormValidationSchema}
            validate={(values) => {
              const deploymentErrors = getDuplicateDeploymentValidationErrors(
                values.deployments
              );

              return deploymentErrors ? { deployments: deploymentErrors } : {};
            }}
            onSubmit={onSubmit}
            enableReinitialize
            validateOnChange
            validateOnBlur
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              errors,
              touched,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form onSubmit={handleSubmit}>
                <ScrollToFieldError />
                <Stack spacing={VERTICAL_SPACING}>
                  <TextField
                    id="id"
                    label="Tool Name"
                    required
                    helperText={
                      isEditMode
                        ? ''
                        : 'Tool Name may only contain letters, numbers, and dashes.'
                    }
                    value={values.id}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="medium"
                    error={Boolean(touched.id && errors.id)}
                    errorMessage={safeGetErrorMessage(errors.id)}
                    readOnly={isEditMode}
                  />

                  <TextField
                    id="description"
                    label="Description"
                    required
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="medium"
                    error={Boolean(touched.description && errors.description)}
                    errorMessage={safeGetErrorMessage(errors.description)}
                  />

                  <TextField
                    id="toolHostName"
                    label="Tool Hostname"
                    required
                    value={values.toolHostName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="medium"
                    error={Boolean(touched.toolHostName && errors.toolHostName)}
                    errorMessage={safeGetErrorMessage(errors.toolHostName)}
                  />

                  <TextField
                    id="openIdConnectLoginUrl"
                    label="OpenID Connect Login URL"
                    required
                    value={values.openIdConnectLoginUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="medium"
                    error={Boolean(
                      touched.openIdConnectLoginUrl &&
                        errors.openIdConnectLoginUrl
                    )}
                    errorMessage={safeGetErrorMessage(
                      errors.openIdConnectLoginUrl
                    )}
                  />

                  <TextField
                    id="toolKeySetUrl"
                    label="Tool Key Set URL"
                    required
                    value={values.toolKeySetUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="medium"
                    error={Boolean(
                      touched.toolKeySetUrl && errors.toolKeySetUrl
                    )}
                    errorMessage={safeGetErrorMessage(errors.toolKeySetUrl)}
                  />

                  <TextField
                    id="toolRedirectUrl"
                    label="Tool Redirect URL"
                    required
                    value={values.toolRedirectUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="medium"
                    error={Boolean(
                      touched.toolRedirectUrl && errors.toolRedirectUrl
                    )}
                    errorMessage={safeGetErrorMessage(errors.toolRedirectUrl)}
                  />

                  {isEditMode && (
                    <>
                      <CopyTextField
                        id="clientId"
                        label="Client ID"
                        required={false}
                        defaultValue={values.clientId}
                        readonly
                        onChange={handleChange}
                      />
                      <CopyTextField
                        id="issuer"
                        label="Issuer"
                        required={false}
                        defaultValue={values.issuer}
                        readonly
                        onChange={handleChange}
                      />
                      <CopyTextField
                        id="platformKeySetUrl"
                        label="Platform Key Set URL"
                        required={false}
                        defaultValue={values.platformKeySetUrl}
                        readonly
                        onChange={handleChange}
                      />
                      <CopyTextField
                        id="openIdConnectAuthUrl"
                        label="OpenID Connect Auth URL"
                        required={false}
                        defaultValue={values.openIdConnectAuthUrl}
                        readonly
                        onChange={handleChange}
                      />
                    </>
                  )}

                  <LtiAdvantageSection
                    values={values}
                    handleChange={handleChange}
                  />
                  <Divider aria-hidden="true" flexItem />

                  <Stack gap="12px" padding="8px 0">
                    <Typography variant="h2" sx={TYPOGRAPHY_TOKENS.h3}>
                      Tool Parameters
                    </Typography>

                    {isEditMode && (
                      <ToolDeploymentSection
                        defaultDeploymentId={values.defaultDeploymentId}
                        deployments={values.deployments}
                        deploymentsErrors={
                          (Array.isArray(errors.deployments)
                            ? errors.deployments.map((e) =>
                                e &&
                                typeof e === 'object' &&
                                'rootLocations' in e
                                  ? (e.rootLocations as string)
                                  : undefined
                              )
                            : []) as (string | undefined)[]
                        }
                        setFieldValue={setFieldValue}
                      />
                    )}
                    <FieldArray
                      name="staticCustomParameter"
                      render={(arrayHelpers) => (
                        <Stack gap="12px">
                          {values.staticCustomParameter?.length > 0 && (
                            <Box>
                              <Typography
                                variant="h3"
                                sx={TYPOGRAPHY_TOKENS.h5}
                              >
                                Static Custom Parameter
                              </Typography>
                              <Typography variant="p-sm">
                                These parameters stay the same and don&apos;t
                                change for the tool.
                              </Typography>
                              <Typography
                                variant="h4"
                                sx={{
                                  fontWeight: 600,
                                  marginTop: '16px',
                                  marginBottom: '8px',
                                }}
                              >
                                Parameter Details
                              </Typography>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={6}>
                                  <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Parameter Name{' '}
                                    <span style={{ color: COLORS.red[800] }}>
                                      *
                                    </span>
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 600 }}
                                  >
                                    Parameter Value{' '}
                                    <span style={{ color: COLORS.red[800] }}>
                                      *
                                    </span>
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                          )}
                          {/* Display array-level error for static custom parameters */}
                          <ErrorMessage name="staticCustomParameter">
                            {(msg) => (
                              <Typography
                                variant="p-sm"
                                sx={{
                                  color: COLORS.red[800],
                                  marginTop: '8px',
                                }}
                              >
                                {typeof msg === 'string' ? msg : ''}
                              </Typography>
                            )}
                          </ErrorMessage>
                          {values.staticCustomParameter.map(
                            (param: CustomParam, index: number) => {
                              const itemError =
                                typeof errors.staticCustomParameter?.[index] ===
                                'object'
                                  ? (errors.staticCustomParameter[
                                      index
                                    ] as FormikErrors<CustomParam>)
                                  : undefined;

                              return (
                                <StaticParamRow
                                  key={param.id}
                                  param={param}
                                  index={index}
                                  nameErrorMessage={safeGetErrorMessage(
                                    itemError?.name
                                  )}
                                  valueErrorMessage={safeGetErrorMessage(
                                    itemError?.value
                                  )}
                                  showNameError={Boolean(
                                    touched.staticCustomParameter?.[index]
                                      ?.name && itemError?.name
                                  )}
                                  showValueError={Boolean(
                                    touched.staticCustomParameter?.[index]
                                      ?.value && itemError?.value
                                  )}
                                  isEditMode={isEditMode}
                                  arrayHelpers={arrayHelpers}
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                  onDeleteParameter={handleParameterDeleteClick}
                                />
                              );
                            }
                          )}
                          <Button
                            id="addStaticCustomParameterButton"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() =>
                              arrayHelpers.push({
                                id: generateUUID(),
                                name: '',
                                value: '',
                              })
                            }
                            onFocusVisible={() => {}}
                            size="medium"
                            variant="text"
                            sx={{
                              width: 'fit-content',
                            }}
                          >
                            {values.staticCustomParameter.length <= 0
                              ? 'Add Static Custom Parameter'
                              : 'Add Another Static Parameter'}
                          </Button>
                          {values.staticCustomParameter?.length > 0 && (
                            <Divider aria-hidden="true" flexItem />
                          )}
                        </Stack>
                      )}
                    />
                    <FieldArray
                      name="queryParameter"
                      render={(arrayHelpers) => (
                        <Stack gap="12px">
                          {values.queryParameter?.length > 0 && (
                            <Box>
                              <Typography
                                variant="h3"
                                sx={TYPOGRAPHY_TOKENS.h5}
                              >
                                Query Parameter
                              </Typography>
                              <Typography variant="p-sm">
                                These settings are included as part of the query
                                string and may change even for the same tool.
                              </Typography>
                            </Box>
                          )}
                          {values.queryParameter.map(
                            (param: CustomParam, index: number) => {
                              const itemError =
                                typeof errors.queryParameter?.[index] ===
                                'object'
                                  ? (errors.queryParameter[
                                      index
                                    ] as FormikErrors<CustomParam>)
                                  : undefined;

                              return (
                                <ParamValueRow
                                  key={param.id}
                                  fieldName="queryParameter"
                                  param={param}
                                  index={index}
                                  valueErrorMessage={safeGetErrorMessage(
                                    itemError?.value
                                  )}
                                  showValueError={Boolean(
                                    touched.queryParameter?.[index]?.value &&
                                      itemError?.value
                                  )}
                                  arrayHelpers={arrayHelpers}
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                />
                              );
                            }
                          )}
                          <Button
                            id="addQueryParameterButton"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() =>
                              arrayHelpers.push({
                                id: generateUUID(),
                                value: '',
                              })
                            }
                            onFocusVisible={() => {}}
                            size="medium"
                            variant="text"
                            sx={{
                              width: 'fit-content',
                            }}
                          >
                            {values.queryParameter.length <= 0
                              ? 'Add Query Parameter'
                              : 'Add Another Query Parameter'}
                          </Button>
                          {values.queryParameter?.length > 0 && (
                            <Divider aria-hidden="true" flexItem />
                          )}
                        </Stack>
                      )}
                    />

                    <FieldArray
                      name="payloadParameter"
                      render={(arrayHelpers) => (
                        <Stack gap="12px">
                          {values.payloadParameter?.length > 0 && (
                            <Box>
                              <Typography
                                variant="h3"
                                sx={TYPOGRAPHY_TOKENS.h5}
                              >
                                Payload Parameter
                              </Typography>
                              <Typography variant="p-sm">
                                These settings are included as part of the
                                request body and may change even for the same
                                tool.
                              </Typography>
                            </Box>
                          )}
                          {/* Display array-level error for payload parameters */}
                          <ErrorMessage name="payloadParameter">
                            {(msg) => (
                              <Typography
                                variant="p-sm"
                                sx={{
                                  color: COLORS.red[800],
                                  marginTop: '8px',
                                }}
                              >
                                {typeof msg === 'string' ? msg : ''}
                              </Typography>
                            )}
                          </ErrorMessage>
                          {values.payloadParameter.map(
                            (param: CustomParam, index: number) => {
                              const itemError =
                                typeof errors.payloadParameter?.[index] ===
                                'object'
                                  ? (errors.payloadParameter[
                                      index
                                    ] as FormikErrors<CustomParam>)
                                  : undefined;

                              return (
                                <ParamValueRow
                                  key={param.id}
                                  fieldName="payloadParameter"
                                  param={param}
                                  index={index}
                                  valueErrorMessage={safeGetErrorMessage(
                                    itemError?.value
                                  )}
                                  showValueError={Boolean(
                                    touched.payloadParameter?.[index]?.value &&
                                      itemError?.value
                                  )}
                                  arrayHelpers={arrayHelpers}
                                  setFieldValue={setFieldValue}
                                  setFieldTouched={setFieldTouched}
                                />
                              );
                            }
                          )}
                          <Button
                            id="addPayloadParameterButton"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={() =>
                              arrayHelpers.push({
                                id: generateUUID(),
                                value: '',
                              })
                            }
                            onFocusVisible={() => {}}
                            size="medium"
                            variant="text"
                            sx={{
                              width: 'fit-content',
                            }}
                          >
                            {values.payloadParameter.length <= 0
                              ? 'Add Payload Parameter'
                              : 'Add Another Payload Parameter'}
                          </Button>
                          <Divider aria-hidden="true" flexItem />
                        </Stack>
                      )}
                    />
                  </Stack>

                  <Typography variant="h2" sx={TYPOGRAPHY_TOKENS.h3}>
                    User Access
                  </Typography>
                  <Field
                    as={StyledRadioGroup}
                    name="userAccess"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.userAccess}
                    aria-label="User Access Radio Buttons"
                  >
                    <LabelSizeOverride>
                      <Radio
                        id="persistent"
                        label="Persistent Access: if user launches this tool at least once from a course that references the tool, they will keep access to the tool (even after course enrollment ends)."
                        value="sticky-context"
                      />
                      <Radio
                        id="enrolled"
                        label="Enrolled Access: user has access to the tool when enrolled in a course that references the tool, but loses access when their enrollment in the course ends."
                        value="context"
                      />
                    </LabelSizeOverride>
                  </Field>
                  <Typography variant="h2" sx={TYPOGRAPHY_TOKENS.h3}>
                    Tool Permissions
                  </Typography>
                  {isEditMode ? (
                    <Typography variant="p-md">
                      {values.permissions.length > 0 &&
                        values.permissions
                          .map(
                            (permission: Permission) =>
                              `${permission.manage ?? ''}${
                                permission.launch ?? ''
                              }`
                          )
                          .join(', ')}
                    </Typography>
                  ) : (
                    <Typography variant="p-md">
                      When this tool is added, permissions will be generated to
                      allow for tool management and to launch any resource of
                      this tool. These permissions will be of the following
                      format:
                      <br />
                      LTI13_MANAGE_&lt;ToolName&gt;
                      <br /> LTI13_LAUNCH_&lt;ToolName&gt;
                    </Typography>
                  )}
                  <Typography
                    variant="p-sm"
                    sx={{
                      color: COLORS.red[800],
                      padding: '24px 0px 0px',
                    }}
                  >
                    * Indicates a required field
                  </Typography>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="flex-start"
                    spacing={BUTTON_SPACING}
                  >
                    <Button
                      id="cancel"
                      color="secondary"
                      onClick={() => navigate(ROUTES.tools)}
                    >
                      Cancel
                    </Button>
                    <Button id="submit" type="submit" isLoading={isLoading}>
                      {buttonText}
                    </Button>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
          <DuplicateToolModal
            isOpen={isDuplicateToolModalOpen}
            onClose={() => setIsDuplicateToolModalOpen(false)}
          />
          <DeleteParameterModal
            isOpen={isDeleteParameterModalOpen}
            onClose={resetDeleteParameterModal}
            onConfirm={() =>
              currentArrayHelpers &&
              handleConfirmDeleteParameter(currentArrayHelpers)
            }
          />
        </>
      )}
    </>
  );
};

export default ToolForm;
